import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { clearStoredToken, getStoredToken, migrateStoredToken, storeToken } from "./authStorage";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
const SESSION_EXPIRED_MESSAGE = "Your session expired. Please log in again.";

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    !!getStoredToken()
  );
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuthState = useCallback((message) => {
    clearStoredToken();
    if (message) {
      sessionStorage.setItem("authMessage", message);
    }
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const handleAuthError = useCallback((message = SESSION_EXPIRED_MESSAGE) => {
    clearAuthState(message);
  }, [clearAuthState]);

  const fetchCurrentUser = useCallback(async (token, { requireSuccess = false } = {}) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok && data.status && data.user) {
        setUser(data.user);
        return data.user;
      }

      if (requireSuccess) {
        clearAuthState(data.message || SESSION_EXPIRED_MESSAGE);
      }
      setUser(null);
      return null;
    } catch (err) {
      console.error("Could not load current user:", err);
      if (requireSuccess) {
        clearAuthState(SESSION_EXPIRED_MESSAGE);
      }
      setUser(null);
      return null;
    }
  }, [clearAuthState]);

  useEffect(() => {
    const token = migrateStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    fetchCurrentUser(token, { requireSuccess: true }).finally(() => {
      setIsLoading(false);
    });
  }, [fetchCurrentUser]);

  const login = async (username, password) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();

      if (data.status && data.token) {
        storeToken(data.token);
        sessionStorage.removeItem("authMessage");
        setUser(data.user || null);
        setIsAuthenticated(true);
        if (!data.user) {
          fetchCurrentUser(data.token, { requireSuccess: false });
        }
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("Login failed:", err);
      return false;
    }
  };

  const logout = () => {
    sessionStorage.removeItem("authMessage");
    clearAuthState();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, handleAuthError }}>
      {children}
    </AuthContext.Provider>
  );
};
